/**
 * 클립보드 복사 유틸.
 *
 * navigator.clipboard는 secure context(HTTPS 또는 localhost)에서만 존재한다.
 * 테스트 서버를 http://IP:PORT 로 직접 열면 API 자체가 undefined라 복사가 실패하므로,
 * 그런 환경에서는 레거시 execCommand 경로로 폴백한다.
 *
 * @returns 복사 성공 여부. 실패 시 호출부에서 링크를 직접 복사할 수단을 안내한다.
 */
export const copyText = async (text: string): Promise<boolean> => {
  if (!text) return false;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // 권한 거부/비보안 컨텍스트 → 아래 레거시 폴백으로 진행
  }

  // 레거시 폴백. Firefox는 사용자 제스처 컨텍스트에서만 허용하므로
  // 비동기 작업(예: 링크 생성 API) 이후 호출되면 실패할 수 있다.
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    // 보이지 않게 두되, 포커스 이동으로 화면이 스크롤되지 않도록 고정 배치한다
    textarea.style.cssText =
      "position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:none;opacity:0;";
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    // iOS Safari는 setSelectionRange 없이는 선택되지 않는다
    textarea.setSelectionRange(0, text.length);
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
};

/** 요소 안의 텍스트를 전체 선택한다(복사가 막힌 환경에서 Ctrl+C 안내용). */
export const selectElementText = (element: HTMLElement | null) => {
  if (!element) return;
  try {
    const range = document.createRange();
    range.selectNodeContents(element);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  } catch {
    // 선택 실패는 무시 (사용자가 직접 드래그하면 된다)
  }
};
